import { TAP_RATE_FIXED_CENTS, TAP_RATE_PERCENT } from "../business/pricing";

export type FaqTopicId =
  | "setup-verification"
  | "account-settings"
  | "payments"
  | "payouts"
  | "troubleshooting"
  | "referral-program";

/**
 * A rollout flag a topic or an individual answer is conditioned on. Content
 * describing a program that is not live for a merchant must not reach them
 * through browse, search, or the contact-form topic picker.
 */
export type FaqGate = "referral" | "activationBonus";

/**
 * Resolved state of every gate. A record rather than positional booleans so a
 * new gate is a compile error at each call site instead of a silent leak, and
 * so no caller can transpose two flags of the same type.
 */
export type FaqGates = Record<FaqGate, boolean>;

/** Countries an answer can be worded for. */
export const FAQ_COUNTRIES = ["CA", "US"] as const;
export type FaqCountry = (typeof FAQ_COUNTRIES)[number];

/** A business country as a {@link FaqCountry}, or null for any other value. */
export function asFaqCountry(
  value: string | null | undefined,
): FaqCountry | null {
  return FAQ_COUNTRIES.find((country) => country === value) ?? null;
}

export type FaqItem = {
  q: string;
  a: string;
  /** Curated alternate phrasings and synonyms, indexed (and boosted) by search
   * so related queries surface this item even when the wording differs from the
   * question/answer text. Not shown in the UI. */
  keywords?: string;
  /** Rollout gate this answer depends on. Ungated items always show. */
  gate?: FaqGate;
  /**
   * The answer worded for one country, for an item whose facts differ by
   * country. A merchant cares only about their own country, so the assistant
   * uses this once it knows the merchant's. `a` stays the version covering
   * every country, for readers that don't know it.
   */
  byCountry?: Record<FaqCountry, string>;
};

export type FaqTopic = {
  id: FaqTopicId;
  title: string;
  /** Ionicons glyph name. Typed as string here so shared code takes no Expo dependency. */
  icon: string;
  /** Rollout gate the whole topic depends on. Ungated topics always show. */
  gate?: FaqGate;
  items: FaqItem[];
};

/**
 * Canonical Help & Support FAQ content, grouped by topic. This is the single
 * source of support copy for the v2 Help screen — search, browse, and the
 * contact-form topic picker all derive from it. Editing copy happens here.
 *
 * Every answer must describe behaviour the app actually has. An answer that
 * routes a merchant to a control that does not exist generates the support
 * ticket it was written to prevent.
 */
export const FAQ_TOPICS: FaqTopic[] = [
  {
    id: "setup-verification",
    title: "Setup & verification",
    icon: "shield-checkmark-outline",
    items: [
      {
        q: "What is Tap to Pay and how does it work?",
        a: "Tap to Pay turns your phone into a contactless card reader. Your customer holds their card, phone, or smartwatch up to your phone and the payment goes through. No extra hardware, no card reader to buy.",
        keywords:
          "contactless nfc card reader hardware how does it work accept cards",
      },
      {
        q: 'Why is my account showing as "Pending"?',
        a: "\"Pending\" means we've received your signup info but you haven't finished the remaining steps for your account to be fully functional. Tap the setup banner on the Home screen to see what's outstanding.",
        keywords:
          "pending not active incomplete status finish setup approval waiting review",
      },
      {
        q: "How do I complete identity verification?",
        a: "Open Settings, tap Identity verification, then tap Verify identity. You'll be guided through capturing a photo of your government-issued ID and a quick selfie. Usually takes about two minutes; you don't need to leave the app.",
        keywords:
          "kyc identity verify selfie government id document idv prove who you are",
      },
      {
        q: "What is the $5 welcome bonus and how do I get it?",
        a: "After you take your first Tap to Pay sale, $5 lands in your CPOS balance within 24 hours. It has to be a contactless tap — invoice payments don't count. One bonus per merchant.",
        keywords:
          "free credit reward welcome bonus first payment promo signup gift five dollars",
        gate: "activationBonus",
      },
      {
        q: "How do I connect my bank account?",
        a: "Open Settings, tap Bank account, then add your account. In Canada you'll need your institution, transit, and account numbers. In the US you'll need your routing and account numbers. Your details are encrypted, and we never see your full account number.",
        byCountry: {
          CA: "Open Settings, tap Bank account, then add your account. You'll need your institution, transit, and account numbers. Your details are encrypted, and we never see your full account number.",
          US: "Open Settings, tap Bank account, then add your account. You'll need your routing and account numbers. Your details are encrypted, and we never see your full account number.",
        },
        keywords:
          "link bank account banking transit routing institution account number deposit add setup",
      },
      {
        q: "Does Tap to Pay work on Android and iPhone?",
        a: "Yes, on Android phones with NFC running Android 8.0 or later. If your device has NFC turned on and you've accepted the Tap to Pay permission, you're set. Apple devices require an iPhone XS or newer on iOS 16.4+.",
        keywords:
          "android iphone ios nfc samsung google pixel phone compatibility supported requirements device support minimum version",
      },
      {
        q: "Why won't my phone enable Tap to Pay?",
        a: "Turn NFC on, then make sure you accepted the Tap to Pay permission when the app asked. If you declined it, open CPOS Pay in your phone's system settings and allow it there. If your phone is older than the supported models, Tap to Pay will not turn on at all.",
        keywords:
          "nfc not working cant turn on enable permission denied blocked greyed out",
      },
    ],
  },
  {
    id: "account-settings",
    title: "Account & settings",
    icon: "person-circle-outline",
    items: [
      {
        q: "How do I edit my business info or tax settings?",
        a: "Open Settings, then tap Business info (for your business name, website, product description, or statement descriptor) or Tax (for tax type and rate). Changes save when you tap Save.",
        keywords:
          "change update edit business name website description statement descriptor tax rate settings profile",
      },
      {
        q: "What is Freepay?",
        a: "Freepay adds a fee to card payments to cover your processing costs, so your customers pay them instead of you. Your customer sees one card price at checkout, and Freepay works with Tap to Pay. It’s included with Pro, and you can turn it on in Settings under $0 Processing fees. It’s your responsibility to make sure surcharging is permitted where you do business.",
        keywords:
          "freepay surcharge fee card price processing costs pass on customers save keep more zero processing pro",
      },
      {
        q: "Is Freepay a surcharge?",
        a: "Yes. Freepay adds a fee to card payments to cover your processing costs, and that’s a surcharge. Cash and other non-card payments are never charged the fee. It’s your responsibility to make sure surcharging is permitted where you do business.",
        keywords:
          "surcharge fee card payments cash legal compliance regulations rules allowed permitted",
      },
      {
        q: "How do I turn on Freepay?",
        a: "Open Settings, tap $0 Processing fees, and turn Freepay on. You’ll confirm a short agreement the first time. Freepay is included with Pro and works with Tap to Pay, not invoices.",
        keywords:
          "enable activate turn on freepay toggle agreement attestation pro zero processing fees tap to pay",
      },
      {
        q: "How do I upgrade to Pro, and what do I get?",
        a: "Open Settings, tap Subscription, then Upgrade to Pro. Pro unlocks Freepay and a higher per-payment cap. The full list of benefits is on that page.",
        keywords:
          "upgrade pro subscription plan premium paid tier benefits features unlock",
      },
      {
        q: "Can I cancel my Pro subscription anytime?",
        a: "Yes. No contracts. Open Settings, tap Subscription, then Cancel subscription — we'll hand you to whoever bills your plan (the App Store, Google Play, or the web) to confirm. If that hand-off doesn't open, cancel directly: on iPhone, Settings, tap your name, then Subscriptions; on Android, open the Play Store, tap your profile picture, then Payments and subscriptions, then Subscriptions. Your plan drops back to Free at the end of the current billing period.",
        keywords:
          "cancel subscription downgrade stop billing end plan unsubscribe app store google play",
      },
      {
        q: "Can multiple team members use CPOS Pay on their own phones?",
        a: "You can have multiple team members accept payments on their own devices under one account. It's designed for teams or mobile crews who need to take payments on the go using their personal phones.",
        keywords:
          "team members staff employees multiple users devices phones shared account crew colleagues",
      },
      {
        q: "Is my data secure?",
        a: "Yes. Card data is encrypted end-to-end and we never store full card numbers. Tap to Pay uses the secure element on your phone, the same chip that protects Apple Pay and Google Pay.",
        keywords:
          "security privacy encryption safe protect card data trust safety",
      },
      {
        q: "How do I sign out?",
        a: "Open Settings and tap Sign out at the bottom. You'll be asked to confirm. Any payments you've already taken are safe — they're recorded on your account, not on this phone.",
        keywords: "sign out logout log out exit leave session",
      },
    ],
  },
  {
    id: "payments",
    title: "Payments",
    icon: "card-outline",
    items: [
      {
        q: "How do I take my first payment?",
        a: "Open your Home page and type the amount on the keypad. Tap to Pay is selected by default — tap the button to take an in-person card, phone, or smartwatch tap. To bill someone instead, use the chevron on the right of the button and choose Send Invoice.",
        keywords:
          "take charge accept first sale payment start keypad collect money",
      },
      {
        q: "How do I send out an invoice?",
        a: "Type the amount you want to charge, tap the chevron on the right side of the Tap to Pay button, and choose Send Invoice. The button switches to Send Invoice — tap it to send your customer a payment link by email or SMS.",
        keywords:
          "invoice email sms text bill payment link send request remote charge",
      },
      {
        q: "What's the difference between Tap to Pay and sending an invoice?",
        a: "Tap to Pay accepts a card, phone, or smartwatch in person at the moment of sale. Send Invoice emails or texts your customer a link to pay later.",
        keywords: "difference tap to pay invoice in person link compare versus",
      },
      {
        q: "Can I manually enter my customer card details?",
        a: "CPOS Pay is primarily designed for Tap to Pay. Rather than manual entry, you can generate an invoice link that lets your customer securely complete the payment themselves.",
        keywords:
          "manual entry key in type card number moto phone order virtual terminal enter card details",
      },
      {
        q: "Can I offer a tipping option for my customers?",
        a: "Yes. Open Settings, tap Tip, and turn on Enable tipping to let customers add a tip at checkout.",
        keywords:
          "tip tipping gratuity add tip checkout percentage enable tips",
      },
      {
        q: "What are the fees per transaction?",
        // Built from the shared rate, so this answer and the Subscription
        // screen that shows it cannot quote a merchant two different figures.
        a: `${TAP_RATE_PERCENT}% + ${TAP_RATE_FIXED_CENTS}¢ per tap, the same on every plan. Pro raises the payment limit and includes Freepay, but the rate doesn’t change. You can also see it in Settings under Subscription.`,
        keywords:
          "fees cost rate pricing charge percentage transaction commission",
      },
      {
        q: "How do I refund a customer?",
        a: "Open Activity, tap the transaction you want to refund, then tap Refund. You can refund the full amount or part of it, up to whatever hasn't been refunded already. Refunds usually settle on the customer's card within 5 to 10 business days.",
        keywords:
          "refund partial return money back reverse cancel charge give back",
      },
      {
        q: "What if a customer was charged twice?",
        a: "Refund the extra charge yourself. Open Activity and find the two sales for the same amount, usually a few moments apart. Check with your customer that they were one purchase, since someone can buy the same thing twice. Then tap the duplicate, tap Refund and refund it in full. The refund usually settles on the customer's card within 5 to 10 business days.",
        keywords:
          "charged twice double charge duplicate charge charged two times same sale twice overcharged double tap",
      },
    ],
  },
  {
    id: "payouts",
    title: "Payouts",
    icon: "cash-outline",
    /**
     * Two answers here state payout timing and they must always agree. The
     * figures are Stripe's `delay_days` hold per country (US 2, CA 3), read
     * from connected account settings — not an observed bank arrival. No
     * timestamp we store records when funds actually land: `arrivalDate` is
     * Stripe's estimate stamped at payout creation, and `payout.paid` fires
     * within the hour. Re-read account settings before changing either
     * figure; neither can be derived from the Payout model.
     *
     * Verification alone is not sufficient for payouts to flow. A
     * loss-prevention hold (`payoutState.pausedForReview`) suspends the
     * schedule even on a verified account, so both answers name it.
     *
     * Payout speed does not vary by plan. Nothing sets `delay_days`, and the
     * only schedule write applies `interval: 'daily'` to every merchant on
     * identity verification, so no answer may offer faster payouts on Pro.
     */
    items: [
      {
        q: "When do my payouts arrive?",
        a: "Daily, once your account is verified. Payments you take today reach your bank about two business days later in the US and three in Canada. Weekends and bank holidays add a day or two. If your payouts are on hold while we review your account, the Home screen will say so.",
        byCountry: {
          CA: "Daily, once your account is verified. Payments you take today reach your bank about three business days later. Weekends and bank holidays add a day or two. If your payouts are on hold while we review your account, the Home screen will say so.",
          US: "Daily, once your account is verified. Payments you take today reach your bank about two business days later. Weekends and bank holidays add a day or two. If your payouts are on hold while we review your account, the Home screen will say so.",
        },
        keywords:
          "when paid deposit timing money arrive payout schedule get paid bank daily",
      },
      {
        q: "Do you offer instant payouts?",
        a: "No, CPOS doesn't offer instant payouts. Your money arrives on the standard daily schedule: payments you take today reach your bank about two business days later in the US and three in Canada. Weekends and bank holidays add a day or two. If your payouts are on hold while we review your account, the Home screen will say so.",
        byCountry: {
          CA: "No, CPOS doesn't offer instant payouts. Your money arrives on the standard daily schedule: payments you take today reach your bank about three business days later. Weekends and bank holidays add a day or two. If your payouts are on hold while we review your account, the Home screen will say so.",
          US: "No, CPOS doesn't offer instant payouts. Your money arrives on the standard daily schedule: payments you take today reach your bank about two business days later. Weekends and bank holidays add a day or two. If your payouts are on hold while we review your account, the Home screen will say so.",
        },
        keywords:
          "instant fast same day quick payout immediate express speed up",
      },
      {
        q: "Do I get charged for each payout?",
        a: "No. Regular payouts are free, at a 0% fee.",
        keywords:
          "payout fee cost charge free deposit fee withdrawal fee transfer cost",
      },
      {
        q: "Why hasn't my payout arrived yet?",
        a: "Payouts take about two business days in the US and three in Canada, and weekends and bank holidays add a day or two. If it's been longer than that, confirm your bank account is connected and your identity verification is complete — both are required before funds can release. If your payouts are on hold while we review your account, the Home screen will say so and you can reach us at compliance@cpos.com.",
        byCountry: {
          CA: "Payouts take about three business days, and weekends and bank holidays add a day or two. If it's been longer than that, confirm your bank account is connected and your identity verification is complete — both are required before funds can release. If your payouts are on hold while we review your account, the Home screen will say so and you can reach us at compliance@cpos.com.",
          US: "Payouts take about two business days, and weekends and bank holidays add a day or two. If it's been longer than that, confirm your bank account is connected and your identity verification is complete — both are required before funds can release. If your payouts are on hold while we review your account, the Home screen will say so and you can reach us at compliance@cpos.com.",
        },
        keywords:
          "missing late delayed money not received payout problem where is my money",
      },
      {
        q: "How do I change my bank account?",
        a: "Open Settings, tap Bank account, then add the new account. It replaces the one you had, and every payout from then on lands there. You hold one bank account at a time, so there is nothing to select or remove.",
        keywords:
          "change update switch bank account new banking details edit replace different account",
      },
      {
        q: "Why did I only receive a $5 deposit and not the rest of my funds?",
        a: "The $5 deposit is your signup bonus for completing your first Tap to Pay transaction, and it arrives automatically with no verification needed. Your remaining funds are on the way, but they can only be released once you have completed identity verification in Settings.",
        keywords:
          "five dollars only deposit small amount rest of my money missing funds bonus signup held",
        gate: "activationBonus",
      },
      {
        q: "What's the minimum payout amount?",
        a: "No minimum. Your payout is the sum of payments you took that day, even if it's a few dollars.",
        keywords: "minimum threshold amount limit payout small smallest",
      },
    ],
  },
  {
    id: "troubleshooting",
    title: "Troubleshooting",
    icon: "construct-outline",
    items: [
      {
        q: "Why was a payment declined?",
        a: "A payment can be declined for several reasons: the card was expired, had insufficient funds, or was flagged by the customer's bank. We don't see the specific decline reason; that's between the customer and their bank. Ask them to try a different card.",
        keywords:
          "declined failed rejected card not working insufficient funds error payment issue",
      },
      {
        q: "My identity verification was rejected.",
        a: "Usually means the ID image was blurry, the document type isn't accepted, or the selfie didn't match. You can retry it if you faced an error message. If multiple attempts fail, tap \"Still having issues?\" and our team will help.",
        keywords:
          "kyc identity verification rejected failed denied id problem retry blurry",
      },
      {
        q: "I'm not receiving notifications.",
        a: "Open Settings, tap Notifications, and check both parts of that screen: the Push notifications toggle takes you to your phone's system settings, where CPOS Pay needs permission to send anything at all, and the category toggles below it control what you hear about. Alerts about your account or your money being blocked are always sent. If everything is on, try restarting the app.",
        keywords:
          "notifications push alerts not working missing settings permission silent",
      },
      {
        q: "My device isn't supported for Tap to Pay.",
        a: "You can still get paid by invoice. Type the amount on the Home screen, tap the chevron on the right of the Tap to Pay button, and choose Send Invoice to send your customer a payment link by email or SMS.",
        keywords:
          "device not supported compatibility old phone unsupported nfc requirements what can i do alternative",
      },
    ],
  },
  {
    id: "referral-program",
    title: "Referral program",
    icon: "gift-outline",
    gate: "referral",
    items: [
      {
        q: "How do I refer someone to CPOS Pay?",
        a: "Tap the gift icon at the top of any main screen to see your referral link. Share it via WhatsApp, iMessage, a QR code, or your phone's share sheet. When your friend signs up through your link, takes their first Tap to Pay payment, and verifies their identity, you both get $25.",
        keywords:
          "refer invite friend share link referral program earn invite a friend",
      },
      {
        q: "What do I get for a successful referral?",
        a: "$25 added to your CPOS balance for every friend who signs up through your link, takes their first Tap to Pay payment, and verifies their ID. No limit.",
        keywords:
          "referral reward bonus earn money twenty five dollars friend payout",
      },
      {
        q: "What does my friend get when they sign up through my link?",
        a: "Your friend gets $25 too, after completing the same three steps: signing up through your link, taking a first Tap to Pay payment, and verifying their ID.",
        keywords: "friend reward signup bonus referee benefit what they get",
      },
      {
        q: "When does my referral bonus land?",
        a: "Once your friend finishes all three steps, the referral goes to review. Reviews take 1 to 2 business days, and your $25 is issued shortly after. You can follow the progress on the Referrals screen.",
        keywords:
          "referral bonus timing when paid credit arrive land receive review",
      },
      {
        q: "Why hasn't my referral bonus arrived after my friend signed up?",
        a: "Your friend has to complete all three steps before the bonus releases: signing up, taking a first Tap to Pay payment, and verifying their ID. After that the referral goes to review, which takes 1 to 2 business days. Open the Referrals screen to see which step they are on.",
        keywords:
          "referral bonus missing not received delayed steps why no reward",
      },
      {
        q: "Is there a limit on how many people I can refer?",
        a: "No cap. Refer as many as you'd like.",
        keywords: "referral limit cap maximum how many unlimited no limit",
      },
    ],
  },
];

/**
 * The topics and answers a merchant may see, with every gated entry whose flag
 * is off removed. Gating is applied at both levels — a whole topic (the
 * referral program) and a single answer inside an otherwise ungated topic (the
 * activation bonus) — and a topic left with no visible answers is dropped
 * rather than rendered empty.
 */
export function visibleFaqTopics(gates: FaqGates): FaqTopic[] {
  return FAQ_TOPICS.filter((topic) => isVisible(topic.gate, gates))
    .map((topic) => ({
      ...topic,
      items: topic.items.filter((item) => isVisible(item.gate, gates)),
    }))
    .filter((topic) => topic.items.length > 0);
}

/** Whether an ungated (always shown) or gated entry is visible for these gates. */
export function isVisible(gate: FaqGate | undefined, gates: FaqGates): boolean {
  return gate == null || gates[gate];
}

const TOPIC_TITLE_BY_ID = new Map<FaqTopicId, string>(
  FAQ_TOPICS.map((t) => [t.id, t.title]),
);

export function topicTitle(id: FaqTopicId): string {
  return TOPIC_TITLE_BY_ID.get(id) ?? id;
}
